import type { ColumnFiltersState } from '@tanstack/react-table';
import type { InitialTableState } from 'components/data-table/types';
import type { FormationManagementSearchParams } from './atoms';

/** DataTable column id → POST /queries/geologic_intervals `sort_attribute`. */
export const FORMATION_MANAGEMENT_SORT_FIELD_MAP: Record<string, string> = {
  basinName: 'basin_name',
  geologicInterval: 'geologic_interval',
  positionalIndex: 'positional_index',
  createdAt: 'created_at',
  updatedAt: 'updated_at',
};

const SORT_COLUMN_FROM_API: Record<string, string> = {
  basin_name: 'basinName',
  geologic_interval: 'geologicInterval',
  positional_index: 'positionalIndex',
  created_at: 'createdAt',
  updated_at: 'updatedAt',
};

function paramsToColumnFilters(
  p: FormationManagementSearchParams
): ColumnFiltersState {
  const filters: ColumnFiltersState = [];
  if (p.basins?.length) filters.push({ id: 'basinName', value: p.basins });
  if (p.intervals?.length) {
    filters.push({ id: 'geologicInterval', value: p.intervals });
  }
  if (p.creators?.length) filters.push({ id: 'createdBy', value: p.creators });
  if (p.created_at?.start || p.created_at?.end) {
    filters.push({
      id: 'createdAt',
      value: [p.created_at.start ?? null, p.created_at.end ?? null],
    });
  }
  if (p.updated_at?.start || p.updated_at?.end) {
    filters.push({
      id: 'updatedAt',
      value: [p.updated_at.start ?? null, p.updated_at.end ?? null],
    });
  }
  return filters;
}

export function buildFormationManagementInitialTableState(
  p: FormationManagementSearchParams
): InitialTableState {
  return {
    // columnOrder intentionally omitted — DataTable now follows the `columns` prop order
    // (reordering is off, no seed → "auto"), so the canonical left-to-right order comes
    // from how the columns are declared in the content component.
    columnFilters: paramsToColumnFilters(p),
    sortColumn:
      p.sortAttribute && SORT_COLUMN_FROM_API[p.sortAttribute]
        ? SORT_COLUMN_FROM_API[p.sortAttribute]
        : 'positionalIndex',
    sortDirection: p.sortDirection === 'DESC' ? 'desc' : 'asc',
    pageSize: p.pageSize ?? 25,
    page: p.page ?? 1,
    globalFilter: p.text ?? '',
  };
}

/**
 * Translate the DataTable's column filter state into the snake_case search
 * params expected by POST /queries/geologic_intervals. Unset keys are returned
 * as `undefined` so callers can spread the result onto `params` to also clear
 * filters that have been removed.
 * @param {ColumnFiltersState} filters - Current TanStack column filter state.
 * @return {Partial<FormationManagementSearchParams>} Patch to spread onto params.
 */
export function columnFiltersToFormationParams(
  filters: ColumnFiltersState
): Partial<FormationManagementSearchParams> {
  const result: Partial<FormationManagementSearchParams> = {
    basins: undefined,
    intervals: undefined,
    creators: undefined,
    created_at: undefined,
    updated_at: undefined,
  };

  const parseRange = (raw: unknown) => {
    if (!Array.isArray(raw)) return undefined;
    const [start, end] = raw as [string | null, string | null];
    if (!start && !end) return undefined;
    return { start: start ?? undefined, end: end ?? undefined };
  };

  for (const filter of filters) {
    switch (filter.id) {
      case 'basinName':
        result.basins = (filter.value as string[] | undefined)?.length
          ? (filter.value as string[])
          : undefined;
        break;
      case 'geologicInterval':
        result.intervals = (filter.value as string[] | undefined)?.length
          ? (filter.value as string[])
          : undefined;
        break;
      case 'createdBy':
        result.creators = (filter.value as string[] | undefined)?.length
          ? (filter.value as string[])
          : undefined;
        break;
      case 'createdAt':
        result.created_at = parseRange(filter.value);
        break;
      case 'updatedAt':
        result.updated_at = parseRange(filter.value);
        break;
    }
  }

  return result;
}
